import { host } from "../lib/url";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import tokens from "@vladrss/tokens/tokens.json";
import { formatAge, MAX_MUTES, muteNote, muteWord, mutedBy, snippet, topicLabel, unfollowNote } from "@vladrss/core";
import { LineLength, Seg, TextSize } from "./Controls";
import { Icon } from "./Icon";
import { Opml } from "./Opml";
import { ListenSettings } from "./Listen";
import { FeedIcon } from "./List";
import { api, useOwnerAction, useOwnerMutation, useOwnerQuery } from "../lib/api";
import { useCategories, useFeeds, useSettings, useTopics, type Feed } from "../lib/library";
import { setPrefs, usePrefs, type Prefs } from "../lib/prefs";
import { useKeptScroll } from "../lib/kept";
import { useDialogFocus } from "../lib/dialog";
import { optimisticMute } from "../lib/settings";
import { command } from "../lib/command";
import { rowCache, setView, toast, update, useStore, type Row, type SettingsSection } from "../lib/store";

export function Modal({ title, sub, wide, keepAs, children, onClose }: { title: string; sub?: string; wide?: boolean; /** what its scroll position is kept as, for a refresh */ keepAs?: string; children: ReactNode; onClose: () => void }) {
  const card = useRef<HTMLDivElement>(null);
  const body = useKeptScroll<HTMLDivElement>(keepAs ? `modal:${keepAs}` : "");
  useDialogFocus(card, onClose);
  return (
    <div className="modal" role="presentation">
      <div className="m-scrim" onClick={onClose} />
      <div className={`m-card${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} ref={card} tabIndex={-1}>
        <div className="m-h"><div className="t"><h2>{title}</h2>{sub && <p>{sub}</p>}</div><button className="ib close" onClick={onClose} aria-label="Close" data-key="Esc"><Icon name="x" /></button></div>
        <div className="m-b" ref={body.ref} onScroll={keepAs ? body.onScroll : undefined}>{children}</div>
      </div>
    </div>
  );
}

// ---------- Settings: one screen, each section marked synced or per device ----------
const SECTIONS: { id: SettingsSection; label: string; icon: string; synced: boolean }[] = [
  { id: "sources", label: "Sources", icon: "rss", synced: true },
  { id: "filters", label: "Muted words", icon: "eye", synced: true },
  { id: "library", label: "Library", icon: "stack", synced: true },
  { id: "appearance", label: "Appearance", icon: "moon", synced: true },
  { id: "reading", label: "Reading", icon: "type", synced: false },
];

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const section = useStore((st) => st.settingsSection);
  const focusAdd = useStore((st) => st.sourcesAdd);
  const feeds = useFeeds();
  const settings = useSettings();
  const live = (feeds ?? []).filter((f) => !f.deleted);
  const cur = SECTIONS.find((x) => x.id === section) ?? SECTIONS[0]!;
  const badge: Partial<Record<SettingsSection, string>> = { sources: live.length ? String(live.length) : "", filters: settings?.mutes?.length ? `${settings.mutes.length} muted` : "" };
  const failing = live.filter((f) => f.health.state === "failing").length;
  const nav = (synced: boolean) => SECTIONS.filter((x) => x.synced === synced).map((x) => (
    <button key={x.id} role="tab" aria-selected={x.id === section} className={`sg-i${x.id === section ? " on" : ""}`} data-autofocus={x.id === section && !(focusAdd && section === "sources") ? "" : undefined} onClick={() => update({ settingsSection: x.id, sourcesAdd: false })}>
      <Icon name={x.icon} size={16} /><span>{x.label}</span>
      {x.id === "sources" && failing > 0 ? <span className="sg-warn" title={`${failing} failing`}>{failing}</span> : badge[x.id] ? <small>{badge[x.id]}</small> : null}
    </button>
  ));
  // on a narrow screen the sections are a row that scrolls: the chosen one is brought into view, not left off its edge
  const tabs = useRef<HTMLDivElement>(null);
  useEffect(() => { tabs.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ inline: "nearest", block: "nearest" }); }, [section]);
  return (
    <Modal title="Settings" wide keepAs={`settings:${section}`} onClose={onClose}>
      <div className="sg">
        <div className="sg-nav" ref={tabs} role="tablist" aria-orientation="vertical" aria-label="Settings sections">
          <span className="caps">Synced</span>{nav(true)}
          <span className="caps">This device</span>{nav(false)}
          <span className="sg-build" title={`Built ${new Date(__BUILD__.at).toLocaleString()}`}>VladRSS web{__BUILD__.commit ? ` · ${__BUILD__.commit}` : ""} · {new Date(__BUILD__.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
        </div>
        <section className="sg-body" role="tabpanel" aria-label={cur.label}>
          <header className="sg-h"><h3>{cur.label}</h3><span className="scope" title={cur.synced ? "Kept on the server: your phone and every browser use the same" : "Kept here, because a phone and a big screen want different sizes"}><i className={cur.synced ? "" : "dev"} />{cur.synced ? "Same on all your devices" : "Sized for this screen"}</span></header>
          {section === "sources" && <SourcesSection feeds={live} autoFocus={focusAdd} />}
          {section === "filters" && <MutedWords />}
          {section === "library" && <Library />}
          {section === "reading" && <ReadingSection />}
          {section === "appearance" && <AppearanceSection />}
        </section>
      </div>
    </Modal>
  );
}

function ReadingSection() {
  const p = usePrefs();
  const set = (patch: Partial<Prefs>) => setPrefs(patch);
  return (
    <>
      <div className="set">
        <span className="lab">Text size</span>
        <TextSize />
        <span className="lab">Typeface</span><Seg label="Typeface" value={p.font} options={[["serif", "Serif"], ["sans", "Sans"]]} onChange={(font) => set({ font })} />
        <span className="lab">Line length</span><LineLength />
        <span className="lab">Lists</span><Seg label="Lists" value={p.density} options={[["compact", "Compact", "compact"], ["previews", "Previews", "previews"]]} onChange={(density) => set({ density })} />
        {p.density === "previews" && <><span className="lab">Previews</span><Seg label="Previews" value={p.pv} options={[["short", "Short"], ["long", "Long"]]} onChange={(pv) => set({ pv })} /></>}
      </div>
      <div className="preview prose" aria-hidden="true"><p>The trail drops off the ridge in a long, loamy traverse — roots, a few rock steps, then a fast run-out into the creek bed where the light turns green.</p></div>
      <ListenSettings />
    </>
  );
}

function AppearanceSection() {
  const p = usePrefs();
  const set = (patch: Partial<Prefs>) => setPrefs(patch);
  return (
    <div className="set">
      <span className="lab">Theme</span><Seg label="Theme" value={p.theme} options={[["system", "System", "monitor"], ["dark", "Dark", "moon"], ["light", "Light", "sun"]]} onChange={(theme) => set({ theme })} />
      <span className="lab">Colours</span>
      <div className="pal-pick" role="radiogroup" aria-label="Colours">
        {(Object.entries(tokens.palettes) as [Prefs["palette"], (typeof tokens.palettes)["sage"]][]).map(([name, pal]) => (
          <button key={name} className={`pp${p.palette === name ? " on" : ""}`} role="radio" aria-checked={p.palette === name} onClick={() => set({ palette: name })} title={pal.blurb}>
            <span className="sw" aria-hidden="true"><i style={{ background: pal.dark.bg }} /><i style={{ background: pal.light.bg }} /><i style={{ background: pal.dark.accent }} /></span>
            {pal.label}
          </button>
        ))}
      </div>
      {p.theme !== "light" && <><span className="lab">Pure black in dark mode<small className="lab-sub">Saves power on OLED screens</small></span><input type="checkbox" role="switch" className="switch" aria-label="Pure black in dark mode" checked={p.black} onChange={(e) => set({ black: e.target.checked })} /></>}
      <span className="lab">Reading order</span><Seg label="Reading order" value={p.order} options={[["oldest", "Oldest first", "arrowU"], ["newest", "Newest first", "arrowD"]]} onChange={(order) => set({ order })} />
    </div>
  );
}

// ---------- Manage sources ----------
function SourcesSection({ feeds: live, autoFocus }: { feeds: Feed[]; autoFocus: boolean }) {
  const counts = useOwnerQuery(api.articles.counts, {});
  const cats = useCategories(); // in the sidebar's order
  const failing = live.filter((f) => f.health.state === "failing");
  return (
    <>
      <p className="sg-sub">{live.length} {live.length === 1 ? "source" : "sources"} in {cats.length} {cats.length === 1 ? "category" : "categories"}, checked every 30 minutes.</p>
      <AddSource autoFocus={autoFocus} categories={cats} />
      {failing.map((f) => <Attention key={f.id} feed={f} />)}
      <div className="mg-h"><span className="caps">Following</span><CheckNow /></div>
      <div className="mg">{[...live].sort((a, b) => cats.indexOf(a.category) - cats.indexOf(b.category) || a.title.localeCompare(b.title)).map((f) => <SourceRow key={f.id} feed={f} categories={cats} counts={counts?.find((c) => c.feedId === f.id) ?? { total: 0, saved: 0, unread: 0, unreadLeaving: counts === undefined ? null : 0 }} />)}</div>
      <Opml />
    </>
  );
}

/** A source that has stopped working, shown above the list so it is not one row among twelve. */
function Attention({ feed }: { feed: Feed }) {
  const retry = useOwnerMutation(api.feeds.checkNow);
  const h = feed.health;
  if (h.state !== "failing") return null;
  return (
    <div className="attn" role="group" aria-label={`${feed.title} needs attention`}>
      <Icon name="alert" size={18} />
      <div className="t"><b>{feed.title} needs attention</b><span>Failing since {new Date(h.since).toLocaleString(undefined, { month: "short", day: "numeric" })} · {h.reason}</span></div>
      <button className="btn sm" onClick={() => { void retry({ feedId: feed.id }).then(() => toast(`Check requested for ${feed.title}`, undefined, "refresh"), () => toast("Couldn't request a check · try again")); }}>Retry</button>
    </div>
  );
}

function CheckNow() {
  const check = useOwnerMutation(api.feeds.checkNow);
  return <button className="btn ghost sm" onClick={() => { void check({}).then(() => toast("Check requested for every source", undefined, "refresh"), () => toast("Couldn't request a check · try again")); }}><Icon name="refresh" size={16} />Check now</button>;
}

function AddSource({ autoFocus, categories }: { autoFocus: boolean; categories: string[] }) {
  const preview = useOwnerAction(api.feeds.preview);
  const follow = useOwnerMutation(api.feeds.follow);
  const [input, setInput] = useState("");
  const [state, setState] = useState<{ busy?: boolean; error?: string; found?: Awaited<ReturnType<typeof preview>> }>({});
  const [category, setCategory] = useState(categories[0] ?? "Uncategorized");
  const [following, setFollowing] = useState(false);
  const find = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    setState({ busy: true });
    try { setState({ found: await preview({ input }) }); }
    catch (err) { setState({ error: (err as { data?: { message?: string } }).data?.message ?? "No feed found there." }); }
  };
  return (
    <div className="add">
      <div className="caps">Add a source</div>
      <form onSubmit={find} className="add-row">
        <input className="inp" autoFocus={autoFocus} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Paste a site or feed address, e.g. bikerumor.com" aria-label="Site or feed address" spellCheck={false} />
        <button className="btn primary" disabled={state.busy}><span className="swap"><span aria-hidden={state.busy}>Find feed</span><span aria-hidden={!state.busy}>Looking…</span></span></button>
      </form>
      {state.error && <p className="err">{state.error}</p>}
      {state.found && (
        <div className="found">
          <div className="t"><b>{state.found.title}</b><span>{state.found.url} · about {state.found.perWeek} a week · {state.found.fullText ? "full text in the feed" : "excerpts; full text fetched when you open"}</span></div>
          {state.found.already ? <span className="tag">Already following</span> : (
            <>
              <input className="inp sm" list="cats" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" />
              <datalist id="cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              <button className="btn primary" disabled={following} onClick={async () => {
                const found = state.found; if (!found || following) return;
                setFollowing(true);
                const result = await command(() => follow({ url: found.url, title: found.title, category, siteUrl: found.siteUrl }), "Couldn't follow this source · try again");
                setFollowing(false);
                if (result.ok) { toast(`Following ${found.title}`); setState({}); setInput(""); }
              }}>Follow</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SourceRow({ feed, categories, counts }: { feed: Feed; categories: string[]; counts: { total: number; saved: number; unread: number; unreadLeaving: number | null } }) {
  const rename = useOwnerMutation(api.feeds.rename);
  const setCategory = useOwnerMutation(api.feeds.setCategory);
  const unfollow = useOwnerMutation(api.feeds.unfollow);
  const retry = useOwnerMutation(api.feeds.checkNow);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const h = feed.health;
  // a working source says so with its dot alone; anything worth knowing gets a line of its own
  const note = h.state === "failing" ? `Failing since ${new Date(h.since).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · ${h.reason}` : h.state === "quiet" ? `Quiet · nothing new in ${h.weeks} weeks` : feed.bodyNote;
  return (
    <>
      <div className="mg-r">
        <div className="mg-n"><FeedIcon feed={feed} size={28} />
          <div className="t">{editing ? (
            <input className="inp sm" autoFocus defaultValue={feed.title} aria-label={`Name for ${feed.title}`}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== feed.title) void command(() => rename({ id: feed.id, title: v }), "Couldn't rename this source · try again"); setEditing(false); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { e.stopPropagation(); setEditing(false); } }} />
          ) : <b>{feed.title}</b>}
          <small><span className={`hd ${h.state}`} role="img" aria-label={h.state === "failing" ? "Failing" : h.state === "quiet" ? "Quiet" : "Working"} />{host(feed.siteUrl ?? feed.url)}{feed.lastNewAt ? ` · newest ${formatAge(feed.lastNewAt, Date.now())}` : ""}</small>
          {note && <small className={`note ${h.state}`}>{note}</small>}</div>
        </div>
        <select className="sel" value={feed.category} onChange={(e) => { const category = e.target.value; void command(() => setCategory({ id: feed.id, category }), "Couldn't move this source · try again"); }} aria-label={`Category for ${feed.title}`}>
          {[...new Set([...categories, feed.category])].map((c) => <option key={c}>{c}</option>)}
        </select>
        <span className="mg-u">{counts.unread}</span>
        <div className="mg-a">
          {h.state === "failing" && <button className="btn sm" onClick={() => { void command(() => retry({ feedId: feed.id }), "Couldn't request a check · try again"); }}>Retry</button>}
          <button className="ib sm" onClick={() => setEditing(true)} aria-label={`Rename ${feed.title}`} data-tip="Rename"><Icon name="pencil" size={16} /></button>
          <button className="ib sm" onClick={() => setConfirm(true)} aria-label={`Unfollow ${feed.title}`} data-tip="Unfollow"><Icon name="minusC" size={16} /></button>
        </div>
      </div>
      {confirm && (
        <div className="mg-confirm"><span>Unfollow <b>{feed.title}</b>? {counts.unreadLeaving === null ? "Article counts are updating." : unfollowNote({ ...counts, unreadLeaving: counts.unreadLeaving })}</span>
          <button className="btn sm" onClick={() => setConfirm(false)}>Cancel</button>
          <button className="btn sm danger" disabled={counts.unreadLeaving === null} onClick={() => { void command(() => unfollow({ id: feed.id }), "Couldn't unfollow this source · try again").then((result) => { if (result.ok) { setConfirm(false); toast(`Unfollowed ${feed.title}`); } }); }}>Unfollow</button>
        </div>
      )}
    </>
  );
}

function MutedWords() {
  const settings = useSettings();
  const editMute = useOwnerMutation(api.settings.editMute, optimisticMute);
  const [v, setV] = useState("");
  const mutes = settings?.mutes ?? [];
  const full = mutes.length >= MAX_MUTES;
  const note = muteNote(mutes.length); // nothing until the list is nearly full
  const pool = useMemo(() => [...rowCache.values()], [v]); // eslint-disable-line react-hooks/exhaustive-deps
  const hits = v.trim().length >= 3 ? pool.filter((r) => mutedBy(r, [v])) : [];
  const edit = (word: string, muted: boolean, undo = true) => {
    void command(() => editMute({ word, muted }), `Couldn't ${muted ? "mute" : "stop muting"} “${word}” · try again`, () => edit(word, muted, undo)).then((result) => {
      if (result.ok) toast(muted ? `Muted “${word}”` : `Showing articles with “${word}” again`, undo ? () => edit(word, !muted, false) : undefined, "eye");
    });
  };
  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const w = muteWord(v);
    if (!settings || !w || mutes.includes(w) || full) return;
    edit(w, true);
    setV("");
  };
  return (
    <div className="mute">
      <p className="lib-n">Articles whose title or preview contains one of these are hidden everywhere and never count as unread. Useful for a source's recurring deals or sponsored posts.</p>
      <form className="add-row" onSubmit={add}><input className="inp" value={v} onChange={(e) => setV(e.target.value)} placeholder="Add a word or phrase, e.g. lifetime access" aria-label="Word or phrase to mute" aria-describedby={note ? "mute-note" : undefined} /><button className="btn" disabled={!settings || full}>Mute</button></form>
      {note && <p className="lib-n" id="mute-note">{full ? <i>{note}</i> : note}</p>}
      {v.trim().length >= 3 && <p className="lib-n">{hits.length ? <>Would hide {hits.length} loaded: {hits.slice(0, 3).map((r) => <i key={r.id}>{r.title.slice(0, 60)} · </i>)}</> : "Nothing loaded matches; future articles that do will be hidden."}</p>}
      {mutes.length > 0 && <div className="mute-list">{mutes.map((m) => <span className="mchip" key={m}><b>{m}</b><button className="ib sm" onClick={() => edit(m, false)} aria-label={`Stop muting ${m}`}><Icon name="x" size={14} /></button></span>)}</div>}
    </div>
  );
}

/**
 * Convex's usage page for this team. Convex offers no API for usage figures (its management
 * API has none, and the dashboard's own is private and tied to a login), so Settings links to it.
 */
const USAGE_URL = "https://dashboard.convex.dev/t/vlad-adzhigirey/settings/usage";

function Library() {
  const settings = useSettings();
  const setKeep = useOwnerMutation(api.settings.setKeepReadDays);
  const keep = settings?.keepReadDays === undefined ? 90 : settings.keepReadDays;
  return (
    <div className="lib">
      <div className="lib-row"><span className="lab">Keep read articles</span>
        <Seg label="Keep read articles" value={keep} options={[[30, "30 days"], [90, "90 days"], [365, "1 year"], [null, "Forever"]]} onChange={(days) => { void command(() => setKeep({ days: days as 30 | 90 | 365 | null }), "Couldn't change retention · try again"); }} />
      </div>
      <p className="lib-n">Unread and saved articles are never removed. The full text of a read article is kept for 30 days and fetched again if you reopen it.</p>
      <div className="lib-row"><span className="lab">Server usage<small className="lab-sub">Free plan: 1 GB of database reads a month (the limit that matters), 512 MB stored</small></span>
        <a className="btn ghost" href={USAGE_URL} target="_blank" rel="noopener noreferrer">Open<Icon name="ext" size={16} /></a>
      </div>
    </div>
  );
}

// ---------- Search (Ctrl/⌘+K) ----------
// Two things only: articles, and the places to jump to (a source, a category, the fixed
// views). Actions live on their buttons and keys, not here.
type Item = { sec: string; label: string; sub?: string; icon?: string; feed?: Feed; run: () => void };

/** A place matches when one of its words starts with what was typed ("nsmb", "bik"), not anywhere inside a word. */
const startsAWord = (text: string, q: string) => text.toLowerCase().split(/[\s·/&,-]+/).some((w) => w.startsWith(q)) || text.toLowerCase().startsWith(q);

export function Palette({ onClose, onOpenArticle }: { onClose: () => void; onOpenArticle: (id: string, ctx?: string[]) => void }) {
  const card = useRef<HTMLDivElement>(null);
  useDialogFocus(card, onClose);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const feeds = useFeeds();
  const topics = useTopics();
  const cats = useCategories();
  // the server is asked once typing pauses, not for every letter (each search reads its hits whole)
  const [asked, setAsked] = useState("");
  useEffect(() => { const t = setTimeout(() => setAsked(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const results = useOwnerQuery(api.articles.search, asked.length >= 2 ? { query: asked, limit: 30 } : "skip") as (Row & { match?: string })[] | undefined;
  const ql = q.trim().toLowerCase();
  const items: Item[] = useMemo(() => {
    const live = (feeds ?? []).filter((f) => !f.deleted);
    const go: Item[] = [
      { sec: "Go to", label: "All sources", icon: "inbox", run: () => setView({ type: "all" }) },
      { sec: "Go to", label: "Saved", icon: "bookmark", run: () => setView({ type: "all" }, "saved") },
      { sec: "Go to", label: "Recently read", icon: "clock", run: () => setView({ type: "recent" }) },
      ...(topics ?? []).map((t) => ({ sec: "Go to", label: topicLabel(t), sub: t.includes(",") ? t : "Topic", icon: "hash", run: () => setView({ type: "topic", name: t }) })),
      ...cats.map((c) => ({ sec: "Go to", label: c, icon: "folder", run: () => setView({ type: "category", name: c }) })),
      ...live.map((f) => ({ sec: "Go to", label: f.title, sub: f.category, feed: f, run: () => setView({ type: "feed", feedId: f.id }) })),
    ];
    // nothing typed: every place, to jump with the arrows; typed: the few places it names, then articles
    const places = ql ? go.filter((i) => startsAWord(i.label, ql)).slice(0, 4) : go;
    const arts: Item[] = (results ?? []).slice(0, 6).map((r) => {
      const f = feeds?.find((x) => x.id === r.feedId);
      // where it matched: the passage in its text, or in its preview (nothing when the title says it)
      const where = r.match ?? (r.title.toLowerCase().includes(ql) ? "" : snippet(r.preview, ql));
      return { sec: "Articles", label: r.title, sub: `${where ? `${where} · ` : ""}${f?.title ?? ""}`, feed: f, run: () => { for (const x of results!) rowCache.set(x.id, x); onOpenArticle(r.id, results!.map((x) => x.id)); } };
    });
    if (results && results.length) arts.push({ sec: "Articles", label: `Show all ${results.length} as a list`, sub: "read through them like a source", icon: "search", run: () => setView({ type: "search", query: asked }) });
    return [...places, ...arts];
  }, [feeds, topics, cats, results, ql, asked, onOpenArticle]);
  useEffect(() => setSel(0), [q]);
  const run = (i: number) => { const it = items[i]; if (!it) return; onClose(); it.run(); };
  let last = "";
  return (
    <div className="modal pal" role="presentation">
      <div className="m-scrim" onClick={onClose} />
      <div className="m-card pal-card" role="dialog" aria-modal="true" aria-label="Search" ref={card} tabIndex={-1}>
        <div className="pal-in"><Icon name="search" /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles or jump to a source" aria-label="Search"
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
            else if (e.key === "Enter") { e.preventDefault(); run(sel); }
            else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
          }} /><kbd>Esc</kbd></div>
        <div className="pal-list" role="listbox">
          {items.length === 0 && <div className="pal-empty"><b>No matches for “{q}”</b>Try words from a headline or the article, or a source name.</div>}
          {items.map((it, i) => {
            const head = it.sec !== last ? <div className="pal-h caps" key={`h${it.sec}`}>{it.sec}</div> : null;
            last = it.sec;
            return [head, (
              <button key={`${it.sec}${i}`} className={`pal-i${i === sel ? " on" : ""}`} role="option" aria-selected={i === sel} onMouseMove={() => setSel(i)} onClick={() => run(i)}>
                {it.feed ? <FeedIcon feed={it.feed} /> : <Icon name={it.icon ?? "chevR"} size={16} />}
                <span className="lb"><span>{hl(it.label, ql)}</span>{it.sub && <small>{hl(it.sub, ql)}</small>}</span>
              </button>
            )];
          })}
        </div>
      </div>
    </div>
  );
}

function hl(s: string, q: string): ReactNode {
  const i = q ? s.toLowerCase().indexOf(q) : -1;
  return i < 0 ? s : <>{s.slice(0, i)}<mark>{s.slice(i, i + q.length)}</mark>{s.slice(i + q.length)}</>;
}

// ---------- Keyboard help ----------
export function HelpModal({ onClose }: { onClose: () => void }) {
  const k = (label: string, ...keys: string[]) => <div className="k" key={label}><span>{label}</span><span>{keys.map((x, i) => (x === "then" ? <i key={i}> then </i> : <kbd key={i}>{x}</kbd>))}</span></div>;
  return (
    <Modal title="Keyboard shortcuts" sub="j and k work everywhere: the list, an article and Focus." wide onClose={onClose}>
      <div className="keys">
        <div><h3 className="caps">Move</h3>{k("Start reading / next", "J")}{k("Previous", "K")}{k("Page down, then next", "Space")}{k("Next / previous section", "N", "P")}{k("Jump to the discussion", "C")}{k("Open selected", "O")}{k("Back", "Esc")}{k("All · Saved · Recently read", "G", "then", "A / S / R")}{k("Search articles and sources", "Ctrl", "K")}</div>
        <div><h3 className="caps">Act</h3>{k("Keep unread / mark read", "M")}{k("Save", "S")}{k("Open original", "V")}{k("Mark all read", "⇧", "A")}{k("Pick rows", "⇧", "↑ / ↓")}{k("Pick every row", "Ctrl", "A")}{k("Undo", "Z")}{k("Check sources", "R")}</div>
        <div><h3 className="caps">Read</h3>{k("Oldest / newest first", "⇧", "O")}{k("Compact · Previews", "1", "2")}{k("Focus", "F")}{k("Sources · List", "[", "\\")}{k("Reading settings", ",")}{k("This help", "?")}</div>
      </div>
    </Modal>
  );
}

export const closeModal = () => update({ modal: null });
